// @phase13-shim — replaces 2,234-line legacy body after P5 cutover.
// Canvas + all designer chrome now comes from the DesignerShellPage +
// DesignerToolRegistry pipeline. Only the canvas slot is page-specific.
import React, { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { DesignerShellPage } from '../../designer';
import SceneCanvas from '../../components/scene_designer/SceneCanvas';
import ThreeGame from '../../game/ThreeGame';
import { useSceneStore } from '../../store/sceneStore';

// Lightweight ScenePreview stub — SceneLibraryPage imports this for its
// preview modal. The full legacy implementation lived in the 2,234-line
// SceneDesignerPage body; this slim version shows scene metadata and
// action buttons without the full editor chrome.
export function ScenePreview({ scene, onClose, onTryAsLearner }) {
  if (!scene) return null;
  return (
    <div className="scene-preview" data-scene-id={scene.scene_id}>
      <div className="scene-preview__header">
        <h3>{scene.display_name || scene.scene_id}</h3>
        <button type="button" className="scene-preview__close" onClick={onClose} aria-label="Close preview">&times;</button>
      </div>
      <div className="scene-preview__body">
        {scene.description && <p className="scene-preview__desc">{scene.description}</p>}
        <dl className="scene-preview__meta">
          <dt>Type</dt><dd>{scene.scene_type || '—'}</dd>
          <dt>Domain</dt><dd>{scene.domain_id || scene.domain || '—'}</dd>
          <dt>Actors</dt><dd>{scene.actors?.length ?? 0}</dd>
          <dt>Timeline events</dt><dd>{scene.timeline?.length ?? 0}</dd>
        </dl>
      </div>
      <div className="scene-preview__actions">
        {onTryAsLearner && (
          <button type="button" className="btn-primary" onClick={onTryAsLearner}>Try as learner</button>
        )}
        <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

// ── Content-aware canvas for non-football domains ───────────────────
//
// Renders actual scene content (slides, quizzes, labs, prompts etc.)
// inside the designer shell by reading ``domain_metadata`` fields.
// Replaces the old empty placeholder so coaches can preview content
// directly from the Scene Library's Edit action.

function ContentAwareCanvas() {
  const scene = useSceneStore((s) => s.scene);
  if (!scene) return null;

  // Content may live under domain_metadata (legacy) OR at the scene top
  // level (synced from tutorials.segments).  Merge both so the canvas
  // always finds the data regardless of storage location.
  const dm = scene.domain_metadata || {};
  const meta = {
    ...dm,
    slides:         dm.slides         || scene.slides         || [],
    steps:          dm.steps          || scene.steps          || [],
    questions:      dm.questions      || scene.questions      || [],
    topic_summary:  dm.topic_summary  || scene.topic_summary  || '',
    key_insight:    dm.key_insight    || scene.key_insight    || '',
    scenario:       dm.scenario       || scene.scenario       || dm.challenge_brief || '',
    starter_prompt: dm.starter_prompt || scene.starter_prompt || dm.starter_code || '',
    editable_fields: dm.editable_fields || scene.editable_fields || [],
    script_text:    dm.script_text    || scene.script_text    || '',
    video_url:      dm.video_url      || scene.video_url      || '',
    resources:      dm.resources      || scene.resources      || [],
  };
  const type = scene.content_type || scene.scene_type || '';

  const containerStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#0f1117',
    color: '#e2e8f0',
    minHeight: 0,
    padding: '2rem',
    overflowY: 'auto',
    gap: '1rem',
  };
  const cardStyle = {
    background: '#1a1d2e',
    border: '1px solid #2d3748',
    borderRadius: '8px',
    padding: '1rem 1.25rem',
  };
  const labelStyle = {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#64748b',
    marginBottom: '0.35rem',
  };
  const preStyle = {
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: '0.82rem',
    lineHeight: 1.6,
    color: '#94a3b8',
    margin: 0,
  };

  if (type === 'slide_deck') {
    const slides = meta.slides || [];
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || 'Slide Deck'}</div>
        {slides.length === 0 && <div style={{ color: '#64748b', fontSize: '0.85rem' }}>No slides yet.</div>}
        {slides.map((slide, i) => {
          const bullets = slide.bullets || slide.bullet_points || [];
          const notes = slide.speaker_notes || slide.notes || '';
          return (
            <div key={i} style={cardStyle}>
              <div style={labelStyle}>Slide {i + 1}{slide.title ? ` — ${slide.title}` : ''}</div>
              {bullets.length > 0 && (
                <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', listStyle: 'disc' }}>
                  {bullets.map((b, j) => (
                    <li key={j} style={{ fontSize: '0.85rem', lineHeight: 1.6, color: '#cbd5e1', marginBottom: 4 }}>{b}</li>
                  ))}
                </ul>
              )}
              {slide.body && <pre style={preStyle}>{slide.body}</pre>}
              {slide.content && <pre style={preStyle}>{slide.content}</pre>}
              {notes && <div style={{ marginTop: '0.5rem' }}><div style={{ ...labelStyle, color: '#22c55e' }}>Speaker Notes</div><pre style={preStyle}>{notes}</pre></div>}
            </div>
          );
        })}
      </div>
    );
  }

  if (type === 'interactive_quiz' || type === 'quiz_panel' || type === 'quiz') {
    const questions = meta.questions || [];
    const qtIds = meta.question_template_ids || [];
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || 'Quiz'}</div>
        {meta.quiz_format && <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: 4 }}>Format: {meta.quiz_format} {meta.quiz_subject ? `· Subject: ${meta.quiz_subject}` : ''}</div>}
        {questions.length === 0 && qtIds.length > 0 && (
          <div style={cardStyle}>
            <div style={labelStyle}>Question Templates ({qtIds.length})</div>
            {qtIds.map((id, i) => (
              <div key={i} style={{ fontSize: '0.82rem', color: '#94a3b8', padding: '2px 0' }}>{i + 1}. {id}</div>
            ))}
          </div>
        )}
        {questions.length === 0 && qtIds.length === 0 && <div style={{ color: '#64748b', fontSize: '0.85rem' }}>No questions yet.</div>}
        {questions.map((q, i) => (
          <div key={i} style={cardStyle}>
            <div style={labelStyle}>Question {i + 1}</div>
            <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>{q.question || q.prompt || q.text}</div>
            {(q.options || q.choices || []).map((opt, j) => {
              const label = typeof opt === 'string' ? opt : (opt.label || opt.text || '');
              const isCorrect = typeof opt === 'object' && opt.correct;
              return (
                <div key={j} style={{ fontSize: '0.82rem', color: isCorrect ? '#22c55e' : '#94a3b8', padding: '2px 0' }}>
                  {String.fromCharCode(65 + j)}. {label} {isCorrect ? '✓' : ''}
                </div>
              );
            })}
            {q.explanation && <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#64748b' }}>{q.explanation}</div>}
          </div>
        ))}
      </div>
    );
  }

  if (type === 'prompt_canvas') {
    const steps = meta.steps || [];
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || 'Prompt Canvas'}</div>
        {meta.topic_summary && <div style={cardStyle}><div style={labelStyle}>Topic Summary</div><div style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>{meta.topic_summary}</div></div>}
        {steps.map((step, i) => {
          const stepTitle = step.title || step.label || '';
          const stepBody = step.prompt || step.content || '';
          return (
            <div key={i} style={cardStyle}>
              <div style={labelStyle}>Step {i + 1}{stepTitle ? ` — ${stepTitle}` : ''}</div>
              {stepBody && <pre style={preStyle}>{stepBody}</pre>}
              {step.expected_output && <div style={{ marginTop: '0.75rem' }}><div style={{ ...labelStyle, color: '#22c55e' }}>Expected Output</div><pre style={preStyle}>{step.expected_output}</pre></div>}
            </div>
          );
        })}
        {meta.key_insight && <div style={{ ...cardStyle, borderColor: '#3b82f6' }}><div style={{ ...labelStyle, color: '#3b82f6' }}>Key Insight</div><div style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>{meta.key_insight}</div></div>}
        {steps.length === 0 && !meta.topic_summary && <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', marginTop: '2rem' }}>No content yet.</div>}
      </div>
    );
  }

  if (type === 'ai_lab') {
    const scenario = meta.scenario || meta.challenge_brief || '';
    const starterPrompt = meta.starter_prompt || meta.starter_code || '';
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || 'AI Lab'}</div>
        {scenario && <div style={cardStyle}><div style={labelStyle}>Scenario / Challenge</div><div style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>{scenario}</div></div>}
        {starterPrompt && <div style={cardStyle}><div style={labelStyle}>Starter Prompt</div><pre style={preStyle}>{starterPrompt}</pre></div>}
        {meta.editable_fields && Array.isArray(meta.editable_fields) && meta.editable_fields.map((f, i) => (
          <div key={i} style={cardStyle}><div style={labelStyle}>{f.label || `Field ${i + 1}`}</div><pre style={preStyle}>{f.default_value || f.value || ''}</pre></div>
        ))}
        {!scenario && !starterPrompt && <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', marginTop: '2rem' }}>No content yet.</div>}
      </div>
    );
  }

  if (type === 'video') {
    const videoUrl = meta.video_url || '';
    const narration = meta.narration || meta.script_text || '';
    const ytMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || 'Video'}</div>
        {ytMatch && (
          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe src={`https://www.youtube.com/embed/${ytMatch[1]}`} title="Video preview" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        )}
        {videoUrl && <div style={cardStyle}><div style={labelStyle}>Video URL</div><a href={videoUrl} style={{ color: '#4f8ef7', fontSize: '0.85rem' }} target="_blank" rel="noopener noreferrer">{videoUrl}</a></div>}
        {!videoUrl && <div style={{ ...cardStyle, borderColor: 'rgba(250,204,21,0.3)' }}><div style={{ ...labelStyle, color: '#fbbf24' }}>Video URL</div><div style={{ fontSize: '0.85rem', color: '#fbbf24' }}>No video URL set. Use the Video URL field on the right panel to add one.</div></div>}
        {narration && <div style={cardStyle}><div style={labelStyle}>Narration / Script</div><pre style={preStyle}>{narration}</pre></div>}
        {meta.transcript && <div style={cardStyle}><div style={labelStyle}>Transcript</div><pre style={preStyle}>{meta.transcript}</pre></div>}
      </div>
    );
  }

  if (type === 'reflection') {
    const prompts = meta.prompts || meta.reflection_prompts || [];
    const bodyMd = meta.body_markdown || '';
    const prompt = meta.prompt || '';
    const scriptText = meta.script_text || '';
    const mainContent = prompt || bodyMd || scriptText;
    const hasOverview = bodyMd && prompt && bodyMd !== prompt;
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || 'Reflection'}</div>
        {meta.min_words > 0 && <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Minimum {meta.min_words} words required</div>}
        {hasOverview && <div style={cardStyle}><div style={labelStyle}>Overview</div><div style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>{bodyMd}</div></div>}
        {mainContent && <div style={cardStyle}><div style={labelStyle}>{hasOverview ? 'Reflection Prompt' : 'Content'}</div><pre style={preStyle}>{mainContent}</pre></div>}
        {prompts.length > 0 && prompts.map((p, i) => (
          <div key={i} style={cardStyle}><div style={labelStyle}>Prompt {i + 1}</div><div style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>{typeof p === 'string' ? p : p.prompt || p.text}</div></div>
        ))}
        {!mainContent && prompts.length === 0 && <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', marginTop: '2rem' }}>No reflection content yet.</div>}
      </div>
    );
  }

  if (type === 'content_panel' || type === 'explanation') {
    const readingBody = meta.body_markdown || meta.content_markdown || meta.reading_body || meta.script_text || '';
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || 'Reading'}</div>
        {readingBody ? (
          <div style={cardStyle}><div style={labelStyle}>Content</div><pre style={preStyle}>{readingBody}</pre></div>
        ) : (
          <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', marginTop: '2rem' }}>No reading content yet — use the Reading body editor on the right.</div>
        )}
      </div>
    );
  }

  if (type === 'curated_resource') {
    const resUrl = meta.url || '';
    const ytMatch = resUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/playlist\?list=)([a-zA-Z0-9_-]+)/);
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || 'Curated Resource'}</div>
        {meta.source && <div style={{ fontSize: '0.82rem', color: '#a78bfa', marginBottom: 4 }}>Source: {meta.source}</div>}
        {ytMatch && (
          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe src={`https://www.youtube.com/embed/${ytMatch[1]}`} title="Resource preview" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        )}
        {resUrl && <div style={cardStyle}><div style={labelStyle}>URL</div><a href={resUrl} style={{ color: '#4f8ef7', fontSize: '0.85rem', wordBreak: 'break-all' }} target="_blank" rel="noopener noreferrer">{resUrl}</a></div>}
        {!resUrl && <div style={{ ...cardStyle, borderColor: 'rgba(250,204,21,0.3)' }}><div style={{ ...labelStyle, color: '#fbbf24' }}>URL</div><div style={{ fontSize: '0.85rem', color: '#fbbf24' }}>No resource URL set yet.</div></div>}
        {meta.summary && <div style={cardStyle}><div style={labelStyle}>Summary</div><div style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>{meta.summary}</div></div>}
        {meta.takeaway && <div style={{ ...cardStyle, borderColor: '#3b82f6' }}><div style={{ ...labelStyle, color: '#3b82f6' }}>Key Takeaway</div><div style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>{meta.takeaway}</div></div>}
      </div>
    );
  }

  if (type === 'module_assessment' || (type === 'interactive_quiz' && meta.quiz_format === 'module_assessment')) {
    const qtIds = meta.question_template_ids || [];
    const questions = meta.questions || [];
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || 'Module Assessment'}</div>
        <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: 4 }}>
          {meta.module_id && <span>Module: <strong>{meta.module_id}</strong> · </span>}
          Pass threshold: <strong>{meta.pass_threshold ? `${(meta.pass_threshold * 100).toFixed(0)}%` : '80%'}</strong>
          {meta.max_retries_before_review && <span> · Max retries: <strong>{meta.max_retries_before_review}</strong></span>}
          {meta.draws_from_question_pool && <span> · <em>Draws from question pool</em></span>}
        </div>
        {meta.instructions && <div style={cardStyle}><div style={labelStyle}>Instructions</div><pre style={preStyle}>{meta.instructions}</pre></div>}
        {questions.length > 0 && questions.map((q, i) => (
          <div key={i} style={cardStyle}>
            <div style={labelStyle}>Question {i + 1}</div>
            <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>{q.question || q.prompt || q.text}</div>
            {(q.options || q.choices || []).map((opt, j) => {
              const optLabel = typeof opt === 'string' ? opt : (opt.label || opt.text || '');
              const isCorrect = typeof opt === 'object' && opt.correct;
              return (
                <div key={j} style={{ fontSize: '0.82rem', color: isCorrect ? '#22c55e' : '#94a3b8', padding: '2px 0' }}>
                  {String.fromCharCode(65 + j)}. {optLabel} {isCorrect ? '✓' : ''}
                </div>
              );
            })}
          </div>
        ))}
        {questions.length === 0 && qtIds.length > 0 && (
          <div style={cardStyle}>
            <div style={labelStyle}>Question Templates ({qtIds.length})</div>
            {qtIds.map((id, i) => (
              <div key={i} style={{ fontSize: '0.82rem', color: '#94a3b8', padding: '2px 0' }}>{i + 1}. {id}</div>
            ))}
          </div>
        )}
        {questions.length === 0 && qtIds.length === 0 && (
          <div style={{ ...cardStyle, borderColor: 'rgba(250,204,21,0.3)' }}>
            <div style={{ ...labelStyle, color: '#fbbf24' }}>Questions</div>
            <div style={{ fontSize: '0.85rem', color: '#fbbf24' }}>
              {meta.draws_from_question_pool
                ? 'Questions are drawn dynamically from the module question pool at runtime.'
                : 'No questions assigned yet. Add question_template_ids to this scene.'}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (type === 'code_lab') {
    const instructionsMd = meta.instructions_md || meta.instructions || '';
    const starterCode = meta.starter_code || '';
    const solutionCode = meta.solution_code || '';
    const language = meta.language || 'python';
    const testCases = meta.test_cases || [];
    const hints = meta.hints || [];
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || 'Code Lab'}</div>
        <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: 4 }}>
          Language: <strong>{language}</strong>
          {meta.sandbox_provider && <span> · Sandbox: <strong>{meta.sandbox_provider}</strong></span>}
          {meta.time_limit_s && <span> · Time limit: <strong>{meta.time_limit_s}s</strong></span>}
        </div>
        {instructionsMd && <div style={cardStyle}><div style={labelStyle}>Instructions</div><pre style={preStyle}>{instructionsMd}</pre></div>}
        {starterCode && <div style={cardStyle}><div style={labelStyle}>Starter Code</div><pre style={{ ...preStyle, color: '#60a5fa' }}>{starterCode}</pre></div>}
        {solutionCode && <div style={cardStyle}><div style={{ ...labelStyle, color: '#22c55e' }}>Solution Code</div><pre style={{ ...preStyle, color: '#86efac' }}>{solutionCode}</pre></div>}
        {testCases.length > 0 && (
          <div style={cardStyle}>
            <div style={labelStyle}>Test Cases ({testCases.length})</div>
            {testCases.map((tc, i) => (
              <div key={i} style={{ fontSize: '0.82rem', color: '#94a3b8', padding: '4px 0', borderBottom: i < testCases.length - 1 ? '1px solid #2d3748' : 'none' }}>
                <strong>{tc.name || `Test ${i + 1}`}</strong>
                {tc.hidden && <span style={{ color: '#f59e0b', marginLeft: 6 }}>(hidden)</span>}
                {tc.input != null && <div>Input: <code>{String(tc.input)}</code></div>}
                {tc.expected_output != null && <div>Expected: <code>{String(tc.expected_output)}</code></div>}
              </div>
            ))}
          </div>
        )}
        {hints.length > 0 && (
          <div style={cardStyle}>
            <div style={labelStyle}>Hints ({hints.length})</div>
            {hints.map((h, i) => (
              <div key={i} style={{ fontSize: '0.82rem', color: '#94a3b8', padding: '2px 0' }}>
                {i + 1}. {typeof h === 'string' ? h : h.text || JSON.stringify(h)}
              </div>
            ))}
          </div>
        )}
        {!instructionsMd && !starterCode && <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', marginTop: '2rem' }}>No code lab content yet.</div>}
      </div>
    );
  }

  if (type === 'capstone') {
    const instructionsMd = meta.instructions_md || meta.instructions || '';
    const deliverables = meta.deliverables || [];
    const milestones = meta.milestones || [];
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || 'Capstone Project'}</div>
        <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: 4 }}>
          {meta.rubric_id && <span>Rubric: <strong>{meta.rubric_id}</strong> · </span>}
          {meta.allow_file_upload !== false && <span>File upload: <strong>enabled</strong></span>}
        </div>
        {instructionsMd && <div style={cardStyle}><div style={labelStyle}>Project Brief</div><pre style={preStyle}>{instructionsMd}</pre></div>}
        {deliverables.length > 0 && (
          <div style={cardStyle}>
            <div style={labelStyle}>Deliverables ({deliverables.length})</div>
            {deliverables.map((d, i) => (
              <div key={i} style={{ fontSize: '0.82rem', color: '#94a3b8', padding: '4px 0' }}>
                <strong>{d.label || d.type || `Deliverable ${i + 1}`}</strong>
                {d.type && <span style={{ color: '#64748b', marginLeft: 6 }}>({d.type})</span>}
                {d.max_size_mb && <span style={{ color: '#64748b', marginLeft: 6 }}>max {d.max_size_mb}MB</span>}
              </div>
            ))}
          </div>
        )}
        {milestones.length > 0 && (
          <div style={cardStyle}>
            <div style={labelStyle}>Milestones ({milestones.length})</div>
            {milestones.map((m, i) => (
              <div key={i} style={{ fontSize: '0.82rem', color: '#94a3b8', padding: '2px 0' }}>
                {i + 1}. {typeof m === 'string' ? m : m.title || m.label || JSON.stringify(m)}
              </div>
            ))}
          </div>
        )}
        {!instructionsMd && deliverables.length === 0 && <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', marginTop: '2rem' }}>No capstone content yet.</div>}
      </div>
    );
  }

  // Fallback: render all domain_metadata fields
  const entries = Object.entries(meta).filter(([k, v]) => v != null && v !== '' && k !== '_id');
  return (
    <div style={containerStyle}>
      <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{scene.display_name || type || 'Scene'}</div>
      <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Type: <strong>{type}</strong></div>
      {entries.length > 0 ? entries.map(([key, val]) => (
        <div key={key} style={cardStyle}>
          <div style={labelStyle}>{key.replace(/_/g, ' ')}</div>
          <pre style={preStyle}>{typeof val === 'object' ? JSON.stringify(val, null, 2) : String(val)}</pre>
        </div>
      )) : (
        <div style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center', marginTop: '2rem' }}>
          No content yet — use the inspector on the right to add content.
        </div>
      )}
    </div>
  );
}

// ── Error boundary for 3D renderer ─────────────────────────────────
//
// ThreeGame requires WebGL and can crash on unsupported devices or
// when actor data is malformed. The boundary catches render-time
// errors and falls back to the 2D Konva canvas so the coach isn't
// locked out of their scene.

class ViewModeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[ViewModeCanvas] renderer crashed:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0f1117',
          color: '#94a3b8',
          gap: '0.75rem',
          padding: '2rem',
        }}>
          <span style={{ fontSize: '2rem' }}>⚠️</span>
          <div style={{ fontWeight: 600, color: '#e2e8f0' }}>3D renderer unavailable</div>
          <div style={{ fontSize: '0.78rem', textAlign: 'center', maxWidth: '22rem' }}>
            {String(this.state.error?.message || this.state.error)}
          </div>
          <button
            style={{
              marginTop: '0.5rem', padding: '0.35rem 1rem', borderRadius: '5px',
              border: '1px solid #2d3748', background: '#1e2130', color: '#e2e8f0',
              cursor: 'pointer', fontSize: '0.8125rem',
            }}
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Back to 2D
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * ViewModeCanvas — domain-aware canvas selector.
 *
 * Football domain:
 *   2d (default) → SceneCanvas  — Konva Stage, top-down playbook view.
 *   3d           → ThreeGame    — Three.js WebGL renderer with 3D actors.
 *
 * All other domains (ai_orchestrator, ai_entrepreneur, etc.):
 *   → GenericCanvas — neutral placeholder; inspector still works.
 *
 * The 3D renderer is wrapped in an error boundary so a WebGL crash
 * (bad data, unsupported device) falls back gracefully to 2D.
 */
function ViewModeCanvas({ sceneId, domainId }) {
  const scene = useSceneStore((s) => s.scene);
  const eventIdx = useSceneStore((s) => s.selectedEventIndex);

  const frame = useMemo(() => {
    if (!scene) return null;
    const event = scene.timeline?.[eventIdx];
    if (!event) return null;
    return {
      event_index: eventIdx,
      actor_states: event.actor_states || {},
      annotations: event.annotations || [],
      timestamp: event.timestamp || 0,
    };
  }, [scene, eventIdx]);

  // Determine the domain from the loaded scene or the URL param fallback.
  const resolvedDomain = scene?.domain_id || domainId || '';
  const isFootball = resolvedDomain === 'football';

  // Non-football domains: render scene content (slides, quizzes, labs,
  // etc.) directly so coaches can preview what they're editing.
  if (!isFootball) {
    return <ContentAwareCanvas />;
  }

  const mode = scene?.editor_view_mode || '2d';
  const hasActors = scene?.actors?.length > 0;

  if (mode === '3d' && hasActors) {
    return (
      <ViewModeErrorBoundary>
        <ThreeGame
          manifest={scene}
          actors={scene.actors}
          frame={frame}
          sceneKey={scene.renderer_key || 'football_3d'}
        />
      </ViewModeErrorBoundary>
    );
  }

  return (
    <>
      <SceneCanvas sceneId={sceneId} />
      {/* Authoring guide for quiz scenes with no defenders placed yet */}
      {!hasActors && scene?.scene_type === 'interactive_quiz' && (
        <div style={{
          position: 'absolute',
          bottom: '1.25rem',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(15,17,23,0.88)',
          border: '1px solid #2d3748',
          borderRadius: '8px',
          padding: '0.75rem 1.25rem',
          color: '#94a3b8',
          fontSize: '0.8125rem',
          textAlign: 'center',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          zIndex: 10,
        }}>
          No defenders yet — use <strong style={{ color: '#e2e8f0' }}>Add Actor</strong> in the inspector to place defenders on the field
        </div>
      )}
    </>
  );
}

export default function SceneDesignerPage() {
  const { sceneId } = useParams();
  const [searchParams] = useSearchParams();
  const domainId = searchParams.get('domain');

  return (
    // Bound the designer to the full viewport height so the CSS grid 1fr rows
    // get a definite block-size and the portrait canvas scale calculates correctly.
    <div style={{ height: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <DesignerShellPage
        sceneId={sceneId}
        canvasSlot={<ViewModeCanvas sceneId={sceneId} domainId={domainId} />}
        userRoles={[]}
        appSurface="coach"
      />
    </div>
  );
}

